import { createContext, useContext } from 'react';
import { VoiceState } from '../../api/voice';
import { RexyVitals } from '../../hooks/useRexySocket';
import { MicMeter } from '../../hooks/usePTT';
import { TelemetryFeedEntry } from '../TelemetryCard';
// TYPE-ONLY, deliberately. A value import here drags api/voiceDaemon (985 lines,
// a class, its own transport) into the runtime import graph of a module that
// App.tsx loads at startup — the same fault ChatSurface's CI-4 cycle-guard names:
// "type-only across the boundary — keep them out of the runtime import graph".
import type { VoiceCall } from '../../api/voiceDaemon';

export interface AppShellLiveValue {
  connected: boolean;
  vitals: RexyVitals | null;
  feed: TelemetryFeedEntry[];
  meter: MicMeter;
  warmth: number;
  active: boolean;
  state: VoiceState;
  userAmplitude: number;
  agentAmplitude: number;
  ptt: () => void;
  error: string | null;
  muted: boolean;
  /** Seconds the box says this mute has stood. Restated by the box on every lane
   *  heartbeat while it stands, so a surface can say HOW LONG and never has to
   *  time it itself. 0 when not muted. */
  mutedStandingS: number;
  toggleMute: () => void;
  partialTranscript: string;
  agentText: string;

  // ── THE PER-AGENT CALL SURFACE (owner-approved contract change, 2026-08-27) ──
  //
  // A call button on an agent's page must reach the SINGLE daemon client that
  // AppShell already owns. Before this the context carried only the sphere's
  // surface (`ptt: () => void`), so the only way for a child to place a call was
  // to construct its own `useVoiceDaemon()` — A SECOND CLIENT TO THE BOX, with
  // its own call-id contract. That is the thing the isolation rule prevents, so
  // the channel is widened rather than duplicated.
  //
  /** Start a call with an EXPLICIT pair. Never a default agent. */
  /** `null` on a surface that cannot place a per-agent call (Pipeline A holds no
   *  such start). EXPLICITLY NULL rather than optional, so a consumer has to
   *  decide what to show instead of forgetting the case exists. */
  start: ((domain: string, agent: string) => Promise<void>) | null;
  /** THE ONE AUTHORITATIVE FACT: does the box hold a call, and WITH WHOM. Every
   *  surface reads this — the orb and each per-agent button — so they cannot
   *  disagree. If a call with BUCK is live, DELTA's button says so. */
  boxCall: VoiceCall | null;
  /** The call was already running when this window arrived, with the box's own
   *  start time. A call nobody can tell apart from one they just made is a call
   *  nobody ends. */
  adopted: { since: number | null } | null;
  /** The daemon's binding intent. NOT the authority on who a call is with —
   *  read `boxCall` for that. Present because the context carries the daemon's
   *  surface, not because it settles anything. */
  daemonBinding: { domain: string; agent: string } | null;

  // ── THE APP'S NAVIGATION IDENTITY — where the owner is standing ──────────
  //
  // WHY IT IS HERE AND NOT DERIVED. The composer row (InputBox) already receives
  // `activeAgent`, but NOT a domain, and the controls that live there need the
  // PAIR: `/api/rexy/agent-loop` is keyed by it and `resolveVoiceBinding` refuses
  // without it. Deriving the domain from the agent would mean a roster lookup —
  // wrong for an agent created tomorrow, and ambiguous for the ones that appear
  // in more than one place. That is `project_domain_agent_pair_drift` exactly.
  //
  // AND IT IS NOT `daemonBinding`. That is the daemon's binding INTENT, seeded
  // HOMEPAGE/REXY; reading it as "which agent's page is this" would be the
  // substituted-default fault wearing a different name.
  //
  // AppShell already receives both as props from App.tsx, so this carries the
  // SAME fact ChatSurface is given — which is what lets the composer read it
  // WITHOUT ChatSurface.tsx being touched.
  navDomain: string;
  navAgent: string | null;
}

export const AppShellLiveContext = createContext<AppShellLiveValue | null>(null);

export function useAppShellLive(): AppShellLiveValue {
  const v = useContext(AppShellLiveContext);
  if (!v) {
    throw new Error('useAppShellLive must be used inside <AppShell>');
  }
  return v;
}
